import React, { useRef, useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { clientsAPI } from '../../services/api';
import { useNotifications } from '../../contexts/NotificationContext';
import toast from 'react-hot-toast';

export type ClientExportFilters = {
  search?: string;
  gremio?: string;
};

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function filenameFromDisposition(header: string | undefined, fallback: string) {
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

type ClientExportMenuProps = {
  filters?: ClientExportFilters;
  className?: string;
  compact?: boolean;
};

export const ClientExportMenu: React.FC<ClientExportMenuProps> = ({
  filters = {},
  className = '',
  compact = false
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { fetchNotifications } = useNotifications();

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setOpen(false);
    setLoading(true);
    const loadingToast = toast.loading(
      format === 'csv' ? 'Generando exportación CSV…' : 'Generando exportación Excel…'
    );
    try {
      const response = await clientsAPI.export({
        format,
        ...filters
      });
      const mime =
        format === 'csv'
          ? 'text/csv;charset=utf-8'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const fallback = `clientes-${new Date().toISOString().slice(0, 10)}.${format}`;
      const filename = filenameFromDisposition(
        response.headers?.['content-disposition'],
        fallback
      );
      triggerBlobDownload(blob, filename);
      toast.success(`Exportación lista: ${filename}`, { id: loadingToast });
      await fetchNotifications();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al exportar clientes', {
        id: loadingToast
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        type="button"
        disabled={loading}
        onClick={() => setOpen((v) => !v)}
        className={
          compact
            ? 'inline-flex h-10 items-center justify-center rounded-xl border-2 border-primary-300 bg-white px-3 text-sm font-semibold text-primary-700 shadow-sm hover:bg-primary-50 disabled:opacity-50'
            : 'inline-flex items-center rounded-xl border-2 border-primary-300 bg-white px-4 py-3 text-sm font-semibold text-primary-700 shadow-sm hover:bg-primary-50 disabled:opacity-50'
        }
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? 'Exportar clientes' : undefined}
      >
        <ArrowDownTrayIcon className={`${compact ? 'h-5 w-5' : 'mr-2 h-5 w-5'} stroke-2`} />
        {!compact && (loading ? 'Exportando…' : 'Exportar')}
        {!compact && <ChevronDownIcon className="ml-1 h-4 w-4" />}
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            aria-label="Cerrar menú exportar"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => handleExport('xlsx')}
            >
              Excel (.xlsx)
            </button>
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => handleExport('csv')}
            >
              CSV (.csv)
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ClientExportMenu;
